/**
 * Front-end script for the newsletter module
 *
 * @see src/Newsletter/NewsletterSignup.php for how we're making our request to the PRH Messaging Web Services
 */

import {
  handleErrors,
  resetErrors,
  validateEmail
} from '../utils/general-utils';

import { trackNewsletterSubmit } from '../../analytics';

/**
 * init
 *
 * @description initialize the script
 */
const init = () => {
  const newsletterBlocks = document.querySelectorAll( '.block__newsletter' );

  if ( !newsletterBlocks ) {
    return;
  }

  handleSubmit( newsletterBlocks );
};

/**
 * handleSubmit
 *
 * @description Loops through each block
 * @param {Object} blocks
 */
const handleSubmit = ( blocks ) => {
  blocks.forEach( ( block ) => {
    const form = block.querySelector( '.email-form__form' );
    submitForm( block, form );
  });
};

/**
 * submitForm
 *
 * @description Handles form submission and errors
 * @param {Object} block
 * @param {Object} form
 */
const submitForm = ( block, form ) => {
  const submitBtn = block.querySelector( '.block__button button' );

  if ( !submitBtn ) {
    return;
  }

  submitBtn.addEventListener( 'click', ( e ) => {
    e.preventDefault();

    const email = block.querySelector( '#email' );
    const isValid = validateEmail( email.value );
    const hasError = block.querySelector( '.has-error' );
    let msg = '';

    const obj = convertFormDataToObj( form );

    if ( isValid ) {
      setupFormSubmission( form )
        .then( ( data ) => {
          // console.log( data );
          if ( data.response.message === 'OK' ) {
            handleSuccess( form );
            trackNewsletterSubmit( data.body, obj );
          } else {
            msg = 'Currently unavailable. Please try again later.';
            handleErrors( form, msg );
          }
        });

      form.reset();

      if ( hasError ) {
        resetErrors( form );
      }
    } else {
      msg = 'Please enter a valid email.';
      handleErrors( form, msg );
    }
  });
};

/**
 * setupFormSubmission
 *
 * @description Sets up our data to send via AJAX to the backend
 * @param {Object} form
 * @return {Object} data
 */
const setupFormSubmission = async( form ) => {
  const obj = convertFormDataToObj( form );
  const body = setupPostBody( obj );

  const ajaxNonce = typeof ajax_object !== 'undefined' ? ajax_object.nonce : null;
  const response = await fetch( `/wp-json/dwt/v2/newsletter-signups`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // eslint-disable-next-line camelcase, no-undef
      'X-WP-Header': ajaxNonce
    },
    credentials: 'same-origin',
    body
  });

  // console.log(response);

  if ( !response.ok ) {
    throw new Error( `HTTP error! status: ${response.status}` );
  }

  const data = await response.json();
  return data;
};

/**
 * setupPostBody
 *
 * Setup post request body with object to post to PRHm
 *
 * @param {Object} obj
 * @return {string} JSON object
 */
const setupPostBody = ( obj ) => {
  const preferences = setupPreferences( obj );

  return JSON.stringify({
    emailAddress: obj.email,
    acquisitionCode: obj.acquisitionCode,
    referringUrl: window.location.href,
    subscriptions: [
      {
        siteId: obj.siteId,
        programId: obj.programId,
        welcomeMessageSendType: obj.welcomeEmail === 'true' ? 1 : 0,
        preferences
      }
    ]
  });
};

/**
 * setupPreferences
 *
 * Setup PRHm preferences array
 *
 * @param {Object} obj
 * @return {Array} preferences array of objects
 */
const setupPreferences = ( obj ) => {
  if ( !obj.preferenceId && !obj.preferenceKey && !obj.preferenceText ) {
    return;
  }

  return [
    {
      preferenceId: obj.preferenceId,
      preferenceKey: obj.preferenceKey,
      preferenceText: obj.preferenceText
    }
  ];
};

/**
 * convertFormDataToObj
 *
 * converts form data from arrays of keys/values to object
 *
 * @param {Object} form
 * @return {Oject} obj
 */
const convertFormDataToObj = ( form ) => {
  const formData = new FormData( form );

  const obj = {};
  formData.forEach( ( value, key ) => {
    obj[key] = value;
  });

  return obj;
};

/**
 * handleSuccess
 *
 * Handles message on successful submit
 *
 * @param {Object} form
 */
const handleSuccess = ( form ) => {
  const wrapper = form.closest( '.newsletter-wrapper' );
  const children = wrapper.children;
  const heading = wrapper.querySelector( 'h4' );
  const thanks = 'Thanks for signing up!';

  if ( heading ) {
    heading.innerText = thanks;
  } else {
    const confirmationHeading = document.createElement( 'h4' );
    confirmationHeading.classList = 'heading-4 newsletter__heading';
    confirmationHeading.innerText = thanks;
    wrapper.appendChild( confirmationHeading );
  }

  showHide( children, 'is-visible', 'is-hidden' );
};

/**
 * showHide
 *
 * Adds/removes classes
 *
 * @param {Object} els
 * @param {string} str1
 * @param {string} str2
 */
const showHide = ( els, str1, str2 ) => {
  [ ...els ].forEach( ( el ) => {
    if ( el.classList.contains( 'newsletter__image-wrapper' ) ||
      el.classList.contains( 'newsletter__heading' ) ) {
      return;
    }
    el.classList.remove( str1 );
    el.classList.add( str2 );
  });
};

init();
